import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface MultilingualString {
  en: string;
  my: string;
}

// Helper function to get localized string
const getLocalizedString = (text: any, lang: string = "en"): string => {
  if (typeof text === "string") {
    return text; // Backward compatibility: if it's a string, return as is
  }
  return text[lang as keyof MultilingualString] || text.en || "";
};

export interface CardProps {
  id: string;
  title: any;
  description: any;
  image: string;
  href: string;
  category?: string;
  tags?: string[];
  meta?: {
    price?: string;
    badge?: string;
    author?: string;
    date?: string;
  };
  onImageError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const Card: React.FC<CardProps> = ({
  // id,
  title,
  description,
  image,
  href,
  category,
  tags = [],
  meta,
  onImageError,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  // Get localized strings
  const localizedTitle = getLocalizedString(title, currentLang);
  const localizedDescription = getLocalizedString(description, currentLang);
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    if (onImageError) {
      onImageError(e);
    } else {
      // Default fallback
      (e.target as HTMLImageElement).src = "/images/placeholder.png";
    }
  };

  // console.log(image);

  return (
    <article className="group bg-[var(--background-primary)] rounded-xl overflow-hidden shadow-[0_4px_6px_var(--shadow-color)] border border-[var(--border-color)] transition-all duration-200 ease-in-out flex flex-col h-full hover:-translate-y-1 hover:shadow-[0_8px_25px_var(--shadow-color)] dark:bg-[var(--background-secondary)] dark:border-[rgba(59,130,246,0.2)]">
      <div className="w-full h-[200px] overflow-hidden bg-[var(--background-secondary)] dark:bg-[rgba(30,41,59,0.5)] md:h-[180px]">
        <img
          src={image}
          alt={localizedTitle}
          onError={handleImageError}
          className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
        />
      </div>
      <div className="p-6 flex-1 flex flex-col md:p-4">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3 leading-[1.4] md:text-xl md:text-[1.25rem] lg:text-2xl">
          <Link
            to={href}
            className="text-[var(--text-primary)] no-underline transition-colors duration-200 hover:text-primary"
          >
            {localizedTitle.length > 50
              ? `${localizedTitle.slice(0, 50)}...`
              : localizedTitle}
          </Link>
        </h2>

        {category && (
          <div className="mb-3">
            <span className="inline-block bg-[var(--gradient-secondary)] text-white px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide">
              {category}
            </span>
          </div>
        )}

        <p className="text-[var(--text-primary)] mb-4 leading-relaxed flex-grow dark:text-[var(--text-primary)]">
          {localizedDescription.length > 100
            ? `${localizedDescription.slice(0, 100)}...`
            : localizedDescription}
        </p>

        {meta && (
          <div className="flex gap-4 flex-wrap items-center my-4 px-3 py-3 bg-[#f8fafc] rounded-lg border border-[#e2e8f0] md:flex-col md:items-start md:gap-2 dark:bg-[rgba(30,41,59,0.5)] dark:border-[rgba(59,130,246,0.2)]">
            {meta.price && (
              <span className="text-xl font-bold text-primary">
                {meta.price}
              </span>
            )}
            {meta.badge && (
              <span className="text-sm px-3 py-1 rounded-full font-medium bg-[#e2e8f0] text-[#64748b]">
                {meta.badge}
              </span>
            )}
            {meta.author && (
              <span className="font-medium text-primary text-sm">
                {meta.author}
              </span>
            )}
            {meta.date && (
              <span className="text-[#64748b] text-sm">{meta.date}</span>
            )}
          </div>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 mb-4">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="inline-block bg-[var(--gradient-secondary)] text-white px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <Link
          to={href}
          className="bg-[var(--primary-color)] text-white border-none px-5 py-3 rounded-lg font-medium cursor-pointer transition-all duration-200 no-underline inline-block text-center self-start mt-auto hover:bg-white  hover:shadow-[0_4px_8px_rgba(37,99,235,0.3)]"
        >
          Read More
        </Link>
      </div>
    </article>
  );
};

export default Card;
